import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import config from '../config/config';
import { useAuth } from '../utils/AuthContext';
import { getToken } from '../utils/auth';

const ChatModal = ({ onClose }) => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([]);
  const [connectionLabel, setConnectionLabel] = useState('Холболт шалгаж байна...');
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [error, setError] = useState('');
  const socketRef = useRef(null);
  const activeConversationIdRef = useRef('');
  const role = String(user?.role || 'user').toLowerCase();
  const isSupportAgent = role === 'admin' || role === 'moderator';
  const updateConversationCollection = (updatedConversation) => {
    if (!updatedConversation?.id) {
      return;
    }

    setConversations((prev) => {
      const exists = prev.some((item) => String(item.id) === String(updatedConversation.id));
      const next = exists
        ? prev.map((item) => (String(item.id) === String(updatedConversation.id) ? updatedConversation : item))
        : [updatedConversation, ...prev];

      return [...next].sort((left, right) => new Date(right.lastMessageAt || 0) - new Date(left.lastMessageAt || 0));
    });
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setConnectionLabel('Нэвтрээд support чат ашиглана');
      setError('Private support чат ашиглахын тулд нэвтэрсэн байх шаардлагатай.');
      return undefined;
    }

    const socket = io(config.socketUrl);
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('chat:subscribe', { token });
      setConnectionLabel('Support ажилтан хайж байна...');
      setError('');
    });

    socket.on('disconnect', () => {
      setConnectionLabel('Холболт тасарсан. Дахин холбогдож байна...');
    });

    socket.on('chat:message', (message) => {
      setMessages((prev) => {
        if (!message || String(message.conversationId || '') !== activeConversationIdRef.current) {
          return prev;
        }

        return [...prev, message];
      });
    });

    socket.on('chat:support-status', (status) => {
      const adminOnline = Number(status?.adminOnline || 0);
      const moderatorOnline = Number(status?.moderatorOnline || 0);

      if (adminOnline > 0) {
        setConnectionLabel('Admin');
        return;
      }

      if (moderatorOnline > 0) {
        setConnectionLabel('Moderator');
        return;
      }

      setConnectionLabel('Support ажилтан оффлайн');
    });

    socket.on('chat:conversations', (items) => {
      const nextItems = items || [];
      setConversations(nextItems);

      if (!isSupportAgent) {
        return;
      }

      const activeId = activeConversationIdRef.current;
      const activeExists = nextItems.some((item) => String(item.id) === activeId);

      if (!activeExists && nextItems[0]?.id) {
        socket.emit('chat:conversation:join', { conversationId: nextItems[0].id });
      }
    });

    socket.on('chat:conversation:selected', ({ conversation, messages: history }) => {
      activeConversationIdRef.current = String(conversation?.id || conversation?._id || '');
      setActiveConversation(conversation || null);
      updateConversationCollection(conversation);
      setMessages(history || []);
    });

    socket.on('chat:conversation:updated', (conversation) => {
      updateConversationCollection(conversation);
      if (String(conversation?.id || '') === activeConversationIdRef.current) {
        setActiveConversation(conversation || null);
      }
    });

    socket.on('chat:error', (payload) => {
      setError(payload?.message || 'Чат ачааллах үед алдаа гарлаа');
    });

    return () => {
      socket.disconnect();
    };
  }, [isSupportAgent]);

  const activeConversationId = String(activeConversation?.id || activeConversation?._id || '');

  const send = () => {
    if (!text.trim()) {
      return;
    }

    if (!activeConversationId && isSupportAgent) {
      setError('Хариу бичих conversation сонгоно уу');
      return;
    }

    socketRef.current?.emit('chat:message', {
      conversationId: activeConversationId,
      text
    });

    setText('');
    setError('');
  };

  const openConversation = (conversationId) => {
    if (!conversationId) {
      return;
    }

    socketRef.current?.emit('chat:conversation:join', { conversationId });
    setError('');
  };

  const assignToMe = () => {
    if (!activeConversationId) {
      return;
    }

    socketRef.current?.emit('chat:conversation:assign-self', { conversationId: activeConversationId });
  };

  const changeStatus = (status) => {
    if (!activeConversationId) {
      return;
    }

    socketRef.current?.emit('chat:conversation:status', { conversationId: activeConversationId, status });
  };

  const renderMessage = (message) => {
    const ownMessage = isSupportAgent
      ? ['admin', 'moderator'].includes(String(message.role || '').toLowerCase())
      : String(message.role || '').toLowerCase() === 'user';

    return (
      <div
        key={message.id}
        style={{
          display: 'flex',
          justifyContent: ownMessage ? 'flex-end' : 'flex-start',
          marginBottom: 10
        }}
      >
        <div
          style={{
            maxWidth: '78%',
            background: ownMessage ? '#dbeafe' : '#f3f4f6',
            color: '#111827',
            borderRadius: 14,
            padding: '10px 12px'
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
            {message.sender} ({message.role})
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>
        </div>
      </div>
    );
  };

  const activeAssignedLabel = activeConversation?.assignedAgent?.username
    ? `${activeConversation.assignedAgent.username} (${activeConversation.assignedRole || activeConversation.assignedAgent.role || ''})`
    : 'Оноогоогүй';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 'min(1040px, 96vw)' }} onClick={(event) => event.stopPropagation()}>
        <h3>Тусламжийн чат</h3>
        <p className="small">Холбогдсон: {connectionLabel}</p>
        {error && <p className="error">{error}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: isSupportAgent ? '300px 1fr' : '1fr', gap: 14 }}>
          {isSupportAgent && (
            <div className="card" style={{ minHeight: 420, maxHeight: 560, overflow: 'auto' }}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Support Inbox</div>
              {conversations.length === 0 && <div>Одоогоор conversation алга</div>}
              {conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => openConversation(conversation.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    marginBottom: 8,
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    background: String(conversation.id) === activeConversationId ? '#e0f2fe' : '#ffffff'
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{conversation.user?.username || 'Unknown user'}</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Status: {conversation.status || 'pending'}</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>{conversation.lastMessagePreview || 'Шинэ thread'}</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Unread: {conversation.supportUnreadCount || 0}</div>
                </button>
              ))}
            </div>
          )}

          <div>
            {activeConversationId && (
              <div className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{activeConversation?.user?.username || 'Support thread'}</div>
                    <div style={{ fontSize: 12 }}>Status: {activeConversation?.status || 'pending'}</div>
                    <div style={{ fontSize: 12 }}>Assigned: {activeAssignedLabel}</div>
                  </div>
                  {isSupportAgent && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={assignToMe}>Надад оноох</button>
                      {activeConversation?.status !== 'closed' ? (
                        <button type="button" onClick={() => changeStatus('closed')}>Хаах</button>
                      ) : (
                        <button type="button" onClick={() => changeStatus('open')}>Нээх</button>
                      )}
                      {activeConversation?.status !== 'pending' && (
                        <button type="button" onClick={() => changeStatus('pending')}>Pending</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div
              className="card"
              style={{
                minHeight: 320,
                maxHeight: 420,
                overflow: 'auto',
                lineHeight: 1.5,
                marginBottom: 10
              }}
            >
              {!activeConversationId && isSupportAgent && <div>Зүүн талаас conversation сонгоно уу.</div>}
              {activeConversationId && messages.length === 0 && <div>Энэ thread дээр мессеж алга.</div>}
              {messages.map(renderMessage)}
            </div>

            <div className="form" style={{ marginTop: 10 }}>
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder={isSupportAgent ? 'Сонгосон хэрэглэгчид хариу бичнэ үү' : 'Support багт мессежээ бичнэ үү'}
                rows={4}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={send} disabled={!user || (isSupportAgent && !activeConversationId)}>Илгээх</button>
                <button type="button" onClick={onClose}>Хаах</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
